import { useNow } from '@/hooks/useNow';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { saleEventPhase, saleEventWindow, type SaleEvent } from '@/seo/saleEvents';
import { formatLocalDay } from '@/seo/text';

const DAY_MS = 86_400_000;

/** Whole days, hours, minutes and seconds in `ms` (never negative). */
export function countdownParts(ms: number): { days: number; hours: number; minutes: number; seconds: number } {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor((total * 1000) / DAY_MS),
    hours: Math.floor(total / 3600) % 24,
    minutes: Math.floor(total / 60) % 60,
    seconds: total % 60,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

export interface SaleCountdownProps {
  event: Pick<SaleEvent, 'name' | 'startDate' | 'endDate' | 'expected'>;
  locale?: Locale;
}

/**
 * "Starts in" / "Ends in" days, hours, minutes and seconds, counted to the
 * event's first or last local day in the locale's time zone; "Ended on …"
 * afterwards. It ticks every second on its own, so only this box re-renders.
 * `role="timer"` keeps screen readers from announcing every tick.
 */
export function SaleCountdown({ event, locale = DEFAULT_LOCALE }: SaleCountdownProps) {
  const now = useNow(1000);
  const phase = saleEventPhase(event, now, locale);

  if (phase === 'ended') {
    return (
      <p className="text-sm font-semibold text-ink-secondary">
        Ended on <time dateTime={event.endDate}>{formatLocalDay(event.endDate)}</time>
      </p>
    );
  }

  const { start, end } = saleEventWindow(event, locale);
  const target = phase === 'upcoming' ? start : end;
  const { days, hours, minutes, seconds } = countdownParts(target.getTime() - now.getTime());
  const label =
    phase === 'upcoming'
      ? `${event.expected ? 'Expected to start' : 'Starts'} in`
      : 'Live now · ends in';
  const units = [
    { value: String(days), unit: days === 1 ? 'day' : 'days' },
    { value: pad(hours), unit: 'hours' },
    { value: pad(minutes), unit: 'min' },
    { value: pad(seconds), unit: 'sec' },
  ];

  return (
    <div role="timer" aria-label={`${event.name}: ${label.toLowerCase()}`} className="inline-flex flex-col gap-2">
      <p className="text-sm font-bold text-ink-secondary">{label}</p>
      <ul className="flex gap-2">
        {units.map(({ value, unit }) => (
          <li
            key={unit}
            className="flex min-w-[4rem] flex-col items-center rounded-xl border border-card-border bg-card px-3 py-2"
          >
            <span className="text-2xl font-extrabold tabular-nums text-ink">{value}</span>
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary">{unit}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SaleCountdown;
